// ═══════════════════════════════════════════════════════════════════
//  modules/classements/ClassementsPage.js
//  ClassementsPage · ClassementMetiers · ClassementAh
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { argent, erreurDe, nomObjet } from "../ah/ah";
import { METIERS, metierInfo, nombre } from "../erp/metiers";

// ⚠️ Les noms d'objets et la liste des métiers sont IMPORTÉS, pas recopiés.
// Une troisième copie du dictionnaire des objets, c'est une troisième
// chose à corriger le jour où le plugin écrira enfin les noms d'affichage.

const MEDAILLES = { 1: "🥇", 2: "🥈", 3: "🥉" };
const COULEUR_RANG = { 1: "#C9A84C", 2: "#B8C0C8", 3: "#B07B4F" };

export function ClassementsPage({ token }) {
  const [vue, setVue] = useState("metiers");

  const vues = [
    { id: "metiers", label: "🛠️ Métiers" },
    { id: "ah", label: "🏪 Hôtel des ventes" },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ color: THEME.text, fontSize: 22, fontWeight: 800, margin: 0 }}>🏆 Classements</h1>
        <span style={{ color: THEME.textMuted, fontSize: 12 }}>ce qui est réellement joué, et réellement échangé</span>
      </div>

      <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${THEME.border}` }}>
        {vues.map((v) => (
          <button key={v.id} onClick={() => setVue(v.id)}
            style={{
              background: vue === v.id ? THEME.panel : "transparent",
              border: `1px solid ${vue === v.id ? THEME.border : "transparent"}`,
              borderBottom: vue === v.id ? `1px solid ${THEME.panel}` : `1px solid ${THEME.border}`,
              color: vue === v.id ? THEME.text : THEME.textMuted,
              borderRadius: "6px 6px 0 0", padding: "8px 16px", cursor: "pointer",
              fontSize: 13, fontWeight: vue === v.id ? 600 : 400, marginBottom: -1,
            }}>{v.label}</button>
        ))}
      </div>

      <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: "0 8px 8px 8px", padding: 20, minHeight: 260 }}>
        {vue === "metiers" && <ClassementMetiers token={token} />}
        {vue === "ah" && <ClassementAh token={token} />}
      </div>
    </div>
  );
}

// ── Un message d'erreur qui dit ce qui a échoué ────────────────────────
function Erreur({ texte }) {
  return (
    <div style={{ color: THEME.red, background: THEME.redDim, border: `1px solid ${THEME.red}`, borderRadius: 8, padding: "14px 18px", fontSize: 13 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>⛔ La route n'a pas répondu correctement</div>
      <code style={{ color: THEME.text, fontSize: 12, wordBreak: "break-word" }}>{texte}</code>
    </div>
  );
}

function Indisponible({ quoi }) {
  return (
    <div style={{ color: THEME.amber, background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
      ⏳ Les tables de {quoi} n'existent pas sur cette base. Elles sont créées au premier démarrage du plugin.
    </div>
  );
}

const champ = {
  background: THEME.surface, border: `1px solid ${THEME.border}`,
  color: THEME.text, borderRadius: 6, padding: "7px 10px", fontSize: 12,
};

function Rang({ n }) {
  return (
    <span style={{ color: COULEUR_RANG[n] || THEME.textDim, fontWeight: n <= 3 ? 700 : 400, fontVariantNumeric: "tabular-nums" }}>
      {MEDAILLES[n] || n}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  LES MÉTIERS
// ═══════════════════════════════════════════════════════════════════════
const SUR_METIERS = [
  { id: "niveau", label: "Niveau" },
  { id: "xp", label: "XP en cours" },
  { id: "carriere", label: "XP de carrière" },
  { id: "argent", label: "Argent gagné" },
  { id: "actions", label: "Actions" },
];

function ClassementMetiers({ token }) {
  const [metier, setMetier] = useState("");
  const [sur, setSur] = useState("niveau");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ sur });
      if (metier) q.set("metier", metier);
      const res = await fetch(`${API_BASE}/api/classements/metiers?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, metier, sur]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  if (data?.unavailable) return <Indisponible quoi="métiers (oj_*)" />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const tous = !data.metier;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={champ} value={metier} onChange={(e) => setMetier(e.target.value)}>
          <option value="">Tous les métiers confondus</option>
          {METIERS.map((m) => <option key={m.key} value={m.key}>{m.emoji} {m.label}</option>)}
        </select>
        <select style={champ} value={sur} onChange={(e) => setSur(e.target.value)}>
          {SUR_METIERS.map((s) => <option key={s.id} value={s.id}>Classer par {s.label.toLowerCase()}</option>)}
        </select>
        {data.somme && (
          <span style={{ marginLeft: "auto", color: THEME.textMuted, fontSize: 12 }}>
            {nombre(data.somme.joueurs)} joueur{data.somme.joueurs > 1 ? "s" : ""}
            {" · "}{nombre(data.somme.xp, 0)} XP{" · "}{argent(data.somme.argent)}
          </span>
        )}
      </div>

      {/* ── Ce que personne ne joue ──────────────────────────────────── */}
      {/* C'est l'information la plus utile de la page, et elle n'apparaît */}
      {/* dans aucun classement : un métier absent n'a jamais été éprouvé, */}
      {/* donc son barème n'a jamais été testé par personne.               */}
      {tous && data.repartition && (
        <div>
          <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>
            Ce qui est joué, métier par métier
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
            {data.repartition.map((r) => {
              const info = metierInfo(r.metier);
              const jamais = r.joueurs === 0 || r.niveau_max === 0;
              return (
                <div key={r.metier} style={{
                  background: THEME.surface, borderRadius: 6, padding: "9px 12px",
                  border: `1px solid ${jamais ? THEME.amber : THEME.border}`,
                  opacity: r.joueurs === 0 ? 0.6 : 1,
                }}>
                  <div style={{ color: THEME.text, fontSize: 12, fontWeight: 700, marginBottom: 3 }}>
                    {info.emoji} {info.label}
                  </div>
                  {r.joueurs === 0 ? (
                    <div style={{ color: THEME.amber, fontSize: 10 }}>personne n'y a touché</div>
                  ) : (
                    <>
                      <div style={{ color: THEME.textMuted, fontSize: 10 }}>
                        {nombre(r.joueurs)} joueur{r.joueurs > 1 ? "s" : ""} · niveau max {r.niveau_max}
                      </div>
                      <div style={{ color: jamais ? THEME.amber : THEME.textDim, fontSize: 10 }}>
                        {jamais ? "aucun niveau atteint" : `${nombre(r.xp, 0)} XP · ${argent(r.argent)}`}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ color: THEME.textDim, fontSize: 10, marginTop: 8, lineHeight: 1.6 }}>
            En ambre : les métiers où personne n'a dépassé le niveau 0. <strong>Leur barème n'a
            jamais été éprouvé en jeu</strong> — c'est là qu'une erreur de calibrage passerait
            inaperçue jusqu'à l'ouverture.
          </div>
        </div>
      )}

      {/* ── Le classement ───────────────────────────────────────────── */}
      {data.lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, background: THEME.surface, borderRadius: 8, padding: 20, textAlign: "center", fontSize: 13 }}>
          Personne n'a encore gagné d'XP {tous ? "" : `en ${metierInfo(data.metier).label.toLowerCase()}`}.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 700 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {(tous
                  ? ["", "Joueur", "Niveaux cumulés", "Meilleur", "Métiers", "XP en cours", "XP carrière", "Argent", "Actions"]
                  : ["", "Joueur", "Niveau", "XP en cours", "XP carrière", "Argent", "Actions"]
                ).map((h, i) => (
                  <th key={i} style={{ padding: "7px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l) => (
                <tr key={l.uuid} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                  <td style={{ padding: "7px 10px", width: 34, textAlign: "center" }}><Rang n={l.rang} /></td>
                  <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap" }}>
                    {l.pseudo}
                    {/* Le prestige remet le niveau à 1 : sans l'afficher, un */}
                    {/* joueur qui a prestigé passerait pour un débutant.      */}
                    {(l.prestige > 0 || l.prestiges > 0) && (
                      <span style={{ background: THEME.goldDim, color: THEME.gold, fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, marginLeft: 6 }}>
                        ★ {l.prestige || l.prestiges}
                      </span>
                    )}
                  </td>
                  {tous ? (
                    <>
                      <td style={{ padding: "7px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.niveaux)}</td>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.niveau_max)}</td>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.metiers)} / 8</td>
                    </>
                  ) : (
                    <td style={{ padding: "7px 10px", color: THEME.text, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{nombre(l.niveau)}</td>
                  )}
                  <td style={{ padding: "7px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.xp, 1)}</td>
                  <td style={{ padding: "7px 10px", color: THEME.gold, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.xp_carriere, 1)}</td>
                  <td style={{ padding: "7px 10px", color: l.argent > 0 ? THEME.emerald : THEME.textDim, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{argent(l.argent)}</td>
                  <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.actions)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  L'HÔTEL DES VENTES
// ═══════════════════════════════════════════════════════════════════════
const SUR_AH = [
  { id: "vendeur", label: "Vendeurs" },
  { id: "acheteur", label: "Acheteurs" },
  { id: "objet", label: "Objets échangés" },
];
const PERIODES = [
  { id: 0, label: "Depuis le début" },
  { id: 7, label: "7 derniers jours" },
  { id: 30, label: "30 derniers jours" },
  { id: 90, label: "90 derniers jours" },
];

function ClassementAh({ token }) {
  const [sur, setSur] = useState("vendeur");
  const [jours, setJours] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ sur, jours: String(jours) });
      const res = await fetch(`${API_BASE}/api/classements/ah?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      setData(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [token, sur, jours]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div style={{ color: THEME.textMuted }}>Chargement...</div>;
  if (data?.unavailable) return <Indisponible quoi="l'hôtel des ventes (oa_*)" />;
  const err = erreurDe(data);
  if (err) return <Erreur texte={err} />;

  const objets = data.sur === "objet";
  const part = (m) => (data.somme.montant > 0 ? Math.round((m / data.somme.montant) * 1000) / 10 : 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select style={champ} value={sur} onChange={(e) => setSur(e.target.value)}>
          {SUR_AH.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select style={champ} value={jours} onChange={(e) => setJours(Number(e.target.value))}>
          {PERIODES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {data.somme && (
          <span style={{ marginLeft: "auto", color: THEME.textMuted, fontSize: 12 }}>
            {nombre(data.somme.ventes)} vente{data.somme.ventes > 1 ? "s" : ""}
            {" · "}{nombre(data.somme.unites)} unités
            {" · "}<strong style={{ color: THEME.gold }}>{argent(data.somme.montant)}</strong>
          </span>
        )}
      </div>

      {data.lignes.length === 0 ? (
        <div style={{ color: THEME.textMuted, background: THEME.surface, borderRadius: 8, padding: 20, textAlign: "center", fontSize: 13 }}>
          Aucune vente sur cette période.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 760 }}>
            <thead>
              <tr style={{ color: THEME.textMuted, textAlign: "left" }}>
                {(objets
                  ? ["", "Objet", "Ventes", "Unités", "Chiffre", "Part", "Prix moyen", "Fourchette", "Vendeurs", "Acheteurs"]
                  : ["", data.sur === "vendeur" ? "Vendeur" : "Acheteur", "Opérations", "Unités", "Chiffre", "Part", "Annonces", data.sur === "vendeur" ? "Acheteurs" : "Vendeurs"]
                ).map((h, i) => (
                  <th key={i} style={{ padding: "7px 10px", fontWeight: 600, fontSize: 10, textTransform: "uppercase", borderBottom: `1px solid ${THEME.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.lignes.map((l, i) => (
                <tr key={l.uuid || l.objet || i} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                  <td style={{ padding: "7px 10px", width: 34, textAlign: "center" }}><Rang n={l.rang} /></td>
                  <td style={{ padding: "7px 10px", color: THEME.text, whiteSpace: "nowrap" }}>
                    {objets ? nomObjet(l.objet) : (l.pseudo || <span style={{ color: THEME.textDim, fontSize: 10 }}>{l.uuid}</span>)}
                  </td>
                  <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.operations)}</td>
                  <td style={{ padding: "7px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{nombre(l.unites)}</td>
                  <td style={{ padding: "7px 10px", color: THEME.emerald, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{argent(l.montant)}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
                    {/* La part dit ce qu'aucun montant ne dit : si l'économie */}
                    {/* tient sur une personne ou sur un objet.                */}
                    <span style={{ color: part(l.montant) >= 50 ? THEME.amber : THEME.textMuted, fontVariantNumeric: "tabular-nums" }}>
                      {part(l.montant)} %
                    </span>
                  </td>
                  {objets ? (
                    <>
                      <td style={{ padding: "7px 10px", color: THEME.text, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{argent(l.prix_moyen)}</td>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                        {argent(l.prix_mini)} → {argent(l.prix_maxi)}
                      </td>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right" }}>{nombre(l.vendeurs)}</td>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right" }}>{nombre(l.acheteurs)}</td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right" }}>{nombre(l.annonces)}</td>
                      <td style={{ padding: "7px 10px", color: THEME.textMuted, textAlign: "right" }}>{nombre(l.contreparties)}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ color: THEME.textDim, fontSize: 10, lineHeight: 1.7, borderTop: `1px solid ${THEME.border}`, paddingTop: 12 }}>
        Les pseudos viennent des tables de l'hôtel des ventes, pas de LuckPerms : c'est celui qu'avait
        le joueur au moment de la vente. Un changement de pseudo n'apparaît qu'à la vente suivante —
        acceptable pour un classement, qui est de toute façon une photo.
        {" "}<strong style={{ color: THEME.textMuted }}>Une part au-dessus de 50 % est signalée</strong> :
        elle veut dire que l'économie tient sur une seule personne ou un seul objet.
      </div>
    </div>
  );
}
